/**
 * Profile fields supplied at registration.
 */
export interface IRegisterProfile {
  firstName?: string;
  lastName?: string;
  isDoctor: boolean;
  language?: string;
  countryId?: number;
  specialtyId?: number;
  isLicenseAccepted: boolean;
}

/**
 * Request body for `POST /api/auth/login`.
 */
export interface ILoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

/**
 * Request body for `POST /api/auth/register`.
 */
export interface IRegisterRequest {
  email: string;
  password: string;
  profile: IRegisterProfile;
}

/**
 * Request body for `POST /api/auth/change-password`.
 */
export interface IChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Request body for `POST /api/auth/forgot-password`.
 */
export interface IForgotPasswordRequest {
  email: string;
}

/**
 * Request body for `POST /api/auth/reset-password`.
 */
export interface IResetPasswordRequest {
  token: string;
  newPassword: string;
}

/**
 * Successful auth response from register / login.
 * Matches the `AuthResponse` DTO on the backend.
 */
export interface IAuthResponse {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
  user: unknown;
}

/**
 * Error body shape shared by all auth failures. See the API contract error-code catalog.
 */
export interface IAuthErrorBody {
  error: string;
  message?: string;
  retryAfterSeconds?: number;
  fields?: Record<string, string>;
}

/**
 * Stable backend error codes that drive UI branching.
 */
export const AUTH_ERROR_CODES = {
  VALIDATION_FAILED: 'auth.validation_failed',
  POLICY_VIOLATION: 'auth.password.policy_violation',
  HISTORY_REUSE: 'auth.password.history_reuse',
  INVALID_CREDENTIALS: 'auth.invalid_credentials',
  EMAIL_IN_USE: 'auth.email_in_use',
  ACCOUNT_LOCKED: 'auth.account_locked',
  ACCOUNT_DEACTIVATED: 'auth.account_deactivated',
  RESET_TOKEN_INVALID: 'auth.reset_token.invalid',
  MAGIC_LINK_DEPRECATED: 'auth.magic_link_deprecated',
} as const;
